/* ============================================================
   Preset ↔ RRULE serde.

   The UI works with a small typed preset (the Todoist/TickTick options — see
   docs/habit-vs-recurring-task.md); storage is always the canonical RRULE string.
   These helpers convert between the two so presets never become a second stored
   format. The set widens to full RRULE later with no schema change.
   ============================================================ */
import type { Weekday } from './types';

export type RecurrencePreset =
  | { freq: 'daily'; interval: number }
  | { freq: 'weekly'; interval: number; byday: Weekday[] }
  | { freq: 'monthly'; interval: number };

const WEEKDAY_SET = new Set<Weekday>(['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']);

/** Serialize a preset to a canonical RRULE string. No DTSTART: the task's own
    `scheduledFor` is the anchor. A stored rule may carry an `UNTIL=`/`COUNT=`
    bound (the parser below ignores it); the presets never add one. Interval is
    coerced to a positive integer. */
export function presetToRRule(preset: RecurrencePreset): string {
  const interval = Math.max(1, Math.floor(preset.interval));
  switch (preset.freq) {
    case 'daily':
      return `FREQ=DAILY;INTERVAL=${interval}`;
    case 'weekly': {
      const days = preset.byday.filter((d) => WEEKDAY_SET.has(d));
      const byday = days.length ? `;BYDAY=${days.join(',')}` : '';
      return `FREQ=WEEKLY;INTERVAL=${interval}${byday}`;
    }
    case 'monthly':
      // No BYMONTHDAY → rrule.js takes the day-of-month from the anchor day.
      return `FREQ=MONTHLY;INTERVAL=${interval}`;
  }
}

/** Parse an RRULE string back into a preset for the editor. Returns null for
    shapes outside the supported preset set (caller can fall back to raw display). */
export function rruleToPreset(rrule: string): RecurrencePreset | null {
  const kv = parseRRuleKV(rrule);
  const freq = kv.FREQ;
  const interval = kv.INTERVAL ? Math.max(1, parseInt(kv.INTERVAL, 10)) : 1;
  if (!Number.isFinite(interval)) return null;

  switch (freq) {
    case 'DAILY':
      return { freq: 'daily', interval };
    case 'WEEKLY': {
      const byday = kv.BYDAY
        ? (kv.BYDAY.split(',').map((d) => d.trim()) as Weekday[]).filter((d) => WEEKDAY_SET.has(d))
        : [];
      return { freq: 'weekly', interval, byday };
    }
    case 'MONTHLY':
      return { freq: 'monthly', interval };
    default:
      return null;
  }
}

/** Split an `A=B;C=D` RRULE body into an upper-cased key/value map. */
function parseRRuleKV(rrule: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of rrule.split(';')) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    const k = part.slice(0, eq).trim().toUpperCase();
    const v = part.slice(eq + 1).trim().toUpperCase();
    if (k) out[k] = v;
  }
  return out;
}
